import { Link } from "react-router-dom";
import { Play, Mic } from "lucide-react";

const SITE_URL = window.location.origin;

function faceAwareThumbnail(url, width = 800, height = 600) {
  if (!url) return url;
  if (url.includes("res.cloudinary.com") && url.includes("/upload/")) {
    return url.replace("/upload/", `/upload/c_fill,g_face,w_${width},h_${height},q_auto/`);
  }
  return url;
}

function extractPullQuote(text) {
  if (!text) return null;
  const sentences = text
    .replace(/\n+/g, " ")
    .split(/(?<=[.!?])\s+/)
    .filter((s) => s.length > 30 && s.length < 200);
  if (sentences.length === 0) return null;
  // Pick the most "powerful" sentence — longest that's under 180 chars
  const sorted = [...sentences].sort((a, b) => b.length - a.length);
  return sorted[0]?.replace(/^[""\u201C\u201D]+|[""\u201C\u201D]+$/g, "").trim();
}

function getCardSize(story, index) {
  const hasImage = story.media_url && story.media_type === "image";
  const longText = story.why && story.why.length > 300;
  const isFeaturedPosition = index === 0;

  if (isFeaturedPosition && hasImage) return "large";
  if (hasImage && longText) return "large";
  if (!hasImage && !longText) return "small";
  return "medium";
}

function StoryCardInner({ story, size }) {
  // Prefer AI-selected pull quote, fall back to local extraction
  const pullQuote = story.pull_quote || extractPullQuote(story.why);
  const isAnon = story.anonymous;
  const displayName = isAnon
    ? (story.stage === "seeking" ? "A searcher" : story.name)
    : story.name;

  const renderMedia = () => {
    if (!story.media_url) {
      // No image — warm cream with a faint amber quotation mark
      return (
        <div className={`w-full ${size === "small" ? "aspect-[3/2]" : "aspect-[4/3]"} relative overflow-hidden flex items-center justify-center`}
          style={{ background: '#FAEEDA' }}>
          <span className="select-none" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '120px', color: '#E91E8C', opacity: 0.4, lineHeight: 1 }}>
            &ldquo;
          </span>
        </div>
      );
    }

    if (story.media_type === "video") {
      return (
        <div className="w-full aspect-[4/3] bg-[#FAF9F6] relative overflow-hidden amber-overlay">
          <video src={story.media_url} className="w-full h-full object-cover" muted preload="metadata" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-[2]">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
              <Play size={20} className="text-[#1F2937] ml-0.5" />
            </div>
          </div>
        </div>
      );
    }

    if (story.media_type === "audio") {
      return (
        <div className="w-full aspect-[3/2] audio-placeholder">
          <div className="flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-[#E91E8C]/10 flex items-center justify-center">
              <Mic size={22} className="text-[#E91E8C]" />
            </div>
            <span className="text-xs uppercase tracking-[0.15em] text-[#4B5563] font-sans">Voice Note</span>
          </div>
        </div>
      );
    }

    const imgH = size === "large" ? 500 : size === "small" ? 400 : 450;
    return (
      <div className={`w-full overflow-hidden amber-overlay`} style={{ aspectRatio: size === "large" ? "4/3.5" : "4/3" }}>
        <img
          src={faceAwareThumbnail(story.media_url, 800, imgH)}
          alt={displayName}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
    );
  };

  return (
    <Link
      to={`/story/${story.id}`}
      data-testid={`story-card-${story.id}`}
      className="story-card group block bg-white overflow-hidden"
    >
      {renderMedia()}

      <div className={`${size === "large" ? "p-8 md:p-10" : "p-6 md:p-8"}`}>
        {/* Name & Role */}
        <div className="mb-4">
          <p className="text-xs uppercase tracking-[0.2em] font-sans font-semibold text-[#E91E8C]">
            {displayName}
          </p>
          {!isAnon && (
            <p className="text-xs text-[#4B5563] mt-1 font-sans">
              {story.role}
              {story.city && story.country && (
                <span className="text-[#4B5563]/60"> &mdash; {story.city}, {story.country}</span>
              )}
            </p>
          )}
          {story.stage === "seeking" && (
            <p data-testid="seeking-label" className="hand-note text-base mt-1" style={{ color: '#E91E8C' }}>
              still figuring it out &#10022;
            </p>
          )}
        </div>

        {/* Pull quote for larger cards, or regular snippet */}
        {size === "large" && pullQuote ? (
          <blockquote dir="auto" className="font-sans text-lg md:text-xl text-[#1F2937] border-l-2 border-[#E91E8C]/40 pl-5" style={{ lineHeight: '1.7', fontWeight: 500 }}>
            &ldquo;{pullQuote}&rdquo;
          </blockquote>
        ) : (
          <p dir="auto" className="font-sans text-base text-[#1F2937] line-clamp-3" style={{ lineHeight: '1.7', fontWeight: 500 }}>
            &ldquo;{story.why}&rdquo;
          </p>
        )}

        <div className="flex items-center justify-between mt-4">
          <span className="text-xs uppercase tracking-[0.15em] font-sans font-bold text-[#E91E8C] group-hover:underline">
            read the why
          </span>
          <button
            data-testid={`whatsapp-share-${story.id}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const snippet = story.why.length > 100 ? story.why.slice(0, 100) + "..." : story.why;
              const shareUrl = `${SITE_URL}/api/stories/${story.id}/share`;
              const text = `"${snippet}" — ${displayName}\n\nRead their story on iambecause: ${shareUrl}`;
              window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
            }}
            className="p-1.5 rounded-full hover:bg-[#E91E8C]/10 transition-colors opacity-0 group-hover:opacity-100"
            title="Share on WhatsApp"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#4B5563] hover:fill-[#E91E8C]"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </button>
        </div>
      </div>
    </Link>
  );
}

export default function MasonryGrid({ stories }) {
  if (!stories || stories.length === 0) return null;

  return (
    <div className="columns-1 md:columns-2 lg:columns-3 gap-6 md:gap-8 space-y-6 md:space-y-8">
      {stories.map((story, i) => {
        const size = getCardSize(story, i);
        return (
          <div
            key={story.id}
            className="break-inside-avoid opacity-0 animate-fade-in-up"
            style={{ animationDelay: `${Math.min(i * 80, 600)}ms`, animationFillMode: "forwards" }}
          >
            <StoryCardInner story={story} size={size} />
          </div>
        );
      })}
    </div>
  );
}
